import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText, Download, Filter, Calendar, Building, Users, DollarSign, 
  TrendingUp, FileSpreadsheet, File, BarChart3, PieChart, Loader2,
  ChevronRight, Printer, Eye, RefreshCw, Landmark, Percent, ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Relatorios() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("comissoes");
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  
  // Filtros
  const [filtros, setFiltros] = useState({
    dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    dataFim: new Date().toISOString().split('T')[0],
    tipo: "",
    uf: "",
    status: ""
  });

  // Estado do grafico de analise visual (Dia/Mes/Ano)
  const [chartGranularity, setChartGranularity] = useState("dia");
  const [chartMetric, setChartMetric] = useState("contratos");
  const [chartHover, setChartHover] = useState(null);

  // Estado do relatorio de Vendas & Indicadores
  const [vendasGran, setVendasGran] = useState("mes"); // dia/semana/mes/ano
  const [vendasMetric, setVendasMetric] = useState("propostas"); // propostas/solicitado/liberado/comissao
  const [vendasHover, setVendasHover] = useState(null);

  // Dados para relatórios
  const [clientes, setClientes] = useState([]);
  const [orgaos, setOrgaos] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [financeiro, setFinanceiro] = useState({ lancamentos: [], comissoes: [], despesas: [] });
  const [contratos, setContratos] = useState([]);
  const [tabelasComissao, setTabelasComissao] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [tiposOperacao, setTiposOperacao] = useState([]);
  const [correspondentes, setCorrespondentes] = useState([]);

  // Carregar dados
  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [clientesRes, orgaosRes, vendasRes, financeiroRes, contratosRes, tabelasRes, bancosRes, tiposRes, correspRes] = await Promise.all([
        axios.get(`${API}/clientes`).catch(() => ({ data: [] })),
        axios.get(`${API}/orgaos`).catch(() => ({ data: [] })),
        axios.get(`${API}/vendas`).catch(() => ({ data: [] })),
        axios.get(`${API}/financeiro`).catch(() => ({ data: {} })),
        axios.get(`${API}/contratos?per_page=5000`).catch(() => ({ data: { contratos: [] } })),
        axios.get(`${API}/tabelas-comissao`).catch(() => ({ data: { tabelas: [] } })),
        axios.get(`${API}/bancos`).catch(() => ({ data: [] })),
        axios.get(`${API}/tipos-operacao`).catch(() => ({ data: { tipos: [] } })),
        axios.get(`${API}/correspondentes`).catch(() => ({ data: { correspondentes: [] } }))
      ]);
      
      setClientes(clientesRes.data || []);
      setOrgaos(orgaosRes.data || []);
      setVendas(vendasRes.data || []);
      setFinanceiro(financeiroRes.data || { lancamentos: [], comissoes: [], despesas: [] });
      setContratos(contratosRes.data?.contratos || contratosRes.data || []);
      setTabelasComissao(tabelasRes.data?.tabelas || []);
      setBancos(bancosRes.data || []);
      setTiposOperacao(tiposRes.data?.tipos || []);
      setCorrespondentes(correspRes.data?.correspondentes || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  // ===== FUNÇÕES DE EXPORTAÇÃO =====
  
  const exportarPDF = (titulo, colunas, dados, orientacao = "portrait") => {
    const doc = new jsPDF(orientacao, "mm", "a4");
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(41, 128, 185);
    doc.text("StiloCred", 14, 15);
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(titulo, 14, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Gerado em: ${new Date().toLocaleString("pt-BR")}`, 14, 32);
    
    if (filtros.dataInicio && filtros.dataFim) {
      doc.text(`Período: ${new Date(filtros.dataInicio).toLocaleDateString("pt-BR")} a ${new Date(filtros.dataFim).toLocaleDateString("pt-BR")}`, 14, 38);
    }
    
    // Tabela
    doc.autoTable({
      head: [colunas],
      body: dados,
      startY: 45,
      theme: "grid",
      headStyles: { 
        fillColor: [41, 128, 185], 
        textColor: 255,
        fontStyle: "bold"
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      styles: { fontSize: 9, cellPadding: 3 }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    }
    
    doc.save(`${titulo.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF exportado com sucesso!");
  };

  const exportarExcel = (titulo, colunas, dados) => {
    const ws = XLSX.utils.aoa_to_sheet([colunas, ...dados]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Relatório");
    
    // Ajustar largura das colunas
    const colWidths = colunas.map((col, i) => ({
      wch: Math.max(col.length, ...dados.map(row => String(row[i] || "").length)) + 2
    }));
    ws["!cols"] = colWidths;
    
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `${titulo.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Excel exportado com sucesso!");
  };

  const visualizarRelatorio = (titulo, colunas, dados) => {
    setPreviewTitle(titulo);
    setPreviewData({ colunas, dados });
    setShowPreview(true);
  };

  // ===== RELATÓRIOS DE CLIENTES =====
  
  const relatorioClientesPorOrgao = () => {
    const agrupado = {};
    clientes.forEach(c => {
      const orgao = c.emprego?.instituicao || "Não informado";
      if (!agrupado[orgao]) agrupado[orgao] = { total: 0, ativos: 0, inativos: 0 };
      agrupado[orgao].total++;
      if (c.status === "ativo") agrupado[orgao].ativos++;
      else agrupado[orgao].inativos++;
    });
    
    const colunas = ["Órgão/Instituição", "Total", "Ativos", "Inativos", "%"];
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([orgao, v]) => [
        orgao, 
        v.total, 
        v.ativos, 
        v.inativos,
        `${((v.ativos / v.total) * 100).toFixed(1)}%`
      ]);
    
    return { titulo: "Clientes por Órgão/Instituição", colunas, dados };
  };

  const relatorioClientesPorUF = () => {
    const agrupado = {};
    clientes.forEach(c => {
      const uf = c.endereco?.uf || "Não informado";
      if (!agrupado[uf]) agrupado[uf] = 0;
      agrupado[uf]++;
    });
    
    const colunas = ["UF", "Total de Clientes", "% do Total"];
    const total = clientes.length || 1;
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1] - a[1])
      .map(([uf, qtd]) => [uf, qtd, `${((qtd / total) * 100).toFixed(1)}%`]);
    
    return { titulo: "Clientes por Estado (UF)", colunas, dados };
  };

  const relatorioClientesPorBeneficio = () => {
    let aposentadorias = 0, pensoes = 0, outros = 0;
    clientes.forEach(c => {
      if (c.beneficio?.possui_aposentadoria) aposentadorias++;
      if (c.beneficio?.possui_pensao) pensoes++;
      if (!c.beneficio?.possui_aposentadoria && !c.beneficio?.possui_pensao) outros++;
    });
    
    const colunas = ["Tipo de Benefício", "Quantidade", "% do Total"];
    const total = clientes.length || 1;
    const dados = [
      ["Aposentadoria", aposentadorias, `${((aposentadorias / total) * 100).toFixed(1)}%`],
      ["Pensão", pensoes, `${((pensoes / total) * 100).toFixed(1)}%`],
      ["Outros/Não informado", outros, `${((outros / total) * 100).toFixed(1)}%`]
    ];
    
    return { titulo: "Clientes por Tipo de Benefício", colunas, dados };
  };

  const relatorioClientesPorStatus = () => {
    const agrupado = { ativo: 0, inativo: 0, pendente: 0 };
    clientes.forEach(c => {
      const status = c.status || "pendente";
      agrupado[status] = (agrupado[status] || 0) + 1;
    });
    
    const colunas = ["Status", "Quantidade", "% do Total"];
    const total = clientes.length || 1;
    const dados = Object.entries(agrupado).map(([status, qtd]) => [
      status.charAt(0).toUpperCase() + status.slice(1),
      qtd,
      `${((qtd / total) * 100).toFixed(1)}%`
    ]);
    
    return { titulo: "Clientes por Status", colunas, dados };
  };

  // ===== RELATÓRIOS FINANCEIROS =====
  
  const relatorioComissoes = () => {
    const comissoes = financeiro.comissoes || [];
    const colunas = ["Data", "Cliente", "Banco", "Valor Operação", "Comissão", "Status"];
    const dados = comissoes.map(c => [
      new Date(c.data).toLocaleDateString("pt-BR"),
      c.cliente || "-",
      c.banco || "-",
      `R$ ${(c.valor_operacao || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
      `R$ ${(c.valor_comissao || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
      c.status || "Pendente"
    ]);
    
    return { titulo: "Relatório de Comissões", colunas, dados };
  };

  const relatorioFluxoCaixa = () => {
    const lancamentos = financeiro.lancamentos || [];
    const colunas = ["Data", "Descrição", "Categoria", "Tipo", "Valor"];
    const dados = lancamentos.map(l => [
      new Date(l.data).toLocaleDateString("pt-BR"),
      l.descricao || "-",
      l.categoria || "-",
      l.tipo === "entrada" ? "Entrada" : "Saída",
      `R$ ${(l.valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
    ]);
    
    return { titulo: "Fluxo de Caixa", colunas, dados };
  };

  const relatorioDespesas = () => {
    const despesas = financeiro.despesas || [];
    const agrupado = {};
    despesas.forEach(d => {
      const cat = d.categoria || "Outros";
      if (!agrupado[cat]) agrupado[cat] = 0;
      agrupado[cat] += d.valor || 0;
    });
    
    const colunas = ["Categoria", "Total", "% do Total"];
    const total = Object.values(agrupado).reduce((a, b) => a + b, 0) || 1;
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1] - a[1])
      .map(([cat, valor]) => [
        cat,
        `R$ ${valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        `${((valor / total) * 100).toFixed(1)}%`
      ]);
    
    return { titulo: "Despesas por Categoria", colunas, dados };
  };

  const relatorioReceitasDespesas = () => {
    const lancamentos = financeiro.lancamentos || [];
    let receitas = 0, despesas = 0;
    lancamentos.forEach(l => {
      if (l.tipo === "entrada") receitas += l.valor || 0;
      else despesas += l.valor || 0;
    });
    
    const colunas = ["Descrição", "Valor"];
    const dados = [
      ["Total de Receitas", `R$ ${receitas.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`],
      ["Total de Despesas", `R$ ${despesas.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`],
      ["Saldo", `R$ ${(receitas - despesas).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`]
    ];
    
    return { titulo: "Receitas x Despesas", colunas, dados };
  };

  // ===== RELATÓRIOS DE VENDAS =====
  
  const relatorioVendasPeriodo = () => {
    const colunas = ["Data", "Cliente", "Produto", "Banco", "Valor", "Status"];
    const dados = vendas.map(v => [
      new Date(v.data || v.created_at).toLocaleDateString("pt-BR"),
      v.cliente || "-",
      v.produto || "-",
      v.banco || "-",
      `R$ ${(v.valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
      v.status || "Pendente"
    ]);
    
    return { titulo: "Vendas por Período", colunas, dados };
  };

  const relatorioVendasVendedor = () => {
    const agrupado = {};
    vendas.forEach(v => {
      const vendedor = v.vendedor || "Não informado";
      if (!agrupado[vendedor]) agrupado[vendedor] = { qtd: 0, valor: 0 };
      agrupado[vendedor].qtd++;
      agrupado[vendedor].valor += v.valor || 0;
    });
    
    const colunas = ["Vendedor", "Qtd Vendas", "Valor Total", "Ticket Médio"];
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].valor - a[1].valor)
      .map(([vendedor, v]) => [
        vendedor,
        v.qtd,
        `R$ ${v.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        `R$ ${(v.valor / v.qtd).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
      ]);
    
    return { titulo: "Vendas por Vendedor", colunas, dados };
  };

  const relatorioPropostasStatus = () => {
    const agrupado = { aguardando: 0, em_analise: 0, aprovada: 0, reprovada: 0, paga: 0 };
    vendas.forEach(v => {
      const status = v.status || "aguardando";
      agrupado[status] = (agrupado[status] || 0) + 1;
    });
    
    const statusLabels = {
      aguardando: "Aguardando",
      em_analise: "Em Análise",
      aprovada: "Aprovada",
      reprovada: "Reprovada",
      paga: "Paga"
    };
    
    const colunas = ["Status", "Quantidade", "% do Total"];
    const total = vendas.length || 1;
    const dados = Object.entries(agrupado).map(([status, qtd]) => [
      statusLabels[status] || status,
      qtd,
      `${((qtd / total) * 100).toFixed(1)}%`
    ]);
    
    return { titulo: "Propostas por Status (Pipeline)", colunas, dados };
  };

  const relatorioTaxaConversao = () => {
    const total = vendas.length || 1;
    const aprovadas = vendas.filter(v => v.status === "aprovada" || v.status === "paga").length;
    const reprovadas = vendas.filter(v => v.status === "reprovada").length;
    const pendentes = vendas.filter(v => !["aprovada", "paga", "reprovada"].includes(v.status)).length;
    
    const colunas = ["Métrica", "Valor"];
    const dados = [
      ["Total de Propostas", total],
      ["Aprovadas/Pagas", aprovadas],
      ["Reprovadas", reprovadas],
      ["Pendentes", pendentes],
      ["Taxa de Conversão", `${((aprovadas / total) * 100).toFixed(1)}%`],
      ["Taxa de Reprovação", `${((reprovadas / total) * 100).toFixed(1)}%`]
    ];
    
    return { titulo: "Taxa de Conversão", colunas, dados };
  };

  // ===== RELATÓRIOS DE COMISSÕES =====
  
  const relatorioComissoesPorBanco = () => {
    const agrupado = {};
    tabelasComissao.forEach(t => {
      const bancoNome = t.banco_nome || bancos.find(b => b.id === t.banco_id)?.nome || t.banco_id || "Não informado";
      if (!agrupado[bancoNome]) {
        agrupado[bancoNome] = { tabelas: 0, regras: 0, tiposOperacao: new Set() };
      }
      agrupado[bancoNome].tabelas++;
      agrupado[bancoNome].regras += (t.comissoes?.length || 0);
      if (t.tipo_operacao) agrupado[bancoNome].tiposOperacao.add(t.tipo_operacao);
    });
    
    const colunas = ["Banco", "Tabelas", "Regras de Comissão", "Tipos de Operação"];
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].tabelas - a[1].tabelas)
      .map(([banco, v]) => [
        banco,
        v.tabelas,
        v.regras,
        v.tiposOperacao.size
      ]);
    
    return { titulo: "Comissões por Banco", colunas, dados };
  };

  const relatorioComissoesPorTipoOperacao = () => {
    const agrupado = {};
    tabelasComissao.forEach(t => {
      const tipo = t.tipo_operacao || "Não informado";
      if (!agrupado[tipo]) {
        agrupado[tipo] = { tabelas: 0, regras: 0, bancos: new Set() };
      }
      agrupado[tipo].tabelas++;
      agrupado[tipo].regras += (t.comissoes?.length || 0);
      if (t.banco_id) agrupado[tipo].bancos.add(t.banco_id);
    });
    
    const colunas = ["Tipo de Operação", "Tabelas", "Regras", "Bancos"];
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].tabelas - a[1].tabelas)
      .map(([tipo, v]) => [
        tipo,
        v.tabelas,
        v.regras,
        v.bancos.size
      ]);
    
    return { titulo: "Comissões por Tipo de Operação", colunas, dados };
  };

  const relatorioComissoesPorCorrespondente = () => {
    const agrupado = {};
    tabelasComissao.forEach(t => {
      const corresp = t.correspondente || "Geral (Sem Correspondente)";
      if (!agrupado[corresp]) {
        agrupado[corresp] = { tabelas: 0, regras: 0 };
      }
      agrupado[corresp].tabelas++;
      agrupado[corresp].regras += (t.comissoes?.length || 0);
    });
    
    const colunas = ["Correspondente", "Tabelas", "Regras de Comissão"];
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].tabelas - a[1].tabelas)
      .map(([corresp, v]) => [
        corresp,
        v.tabelas,
        v.regras
      ]);
    
    return { titulo: "Comissões por Correspondente", colunas, dados };
  };

  const relatorioTabelasComissaoDetalhado = () => {
    const colunas = ["Tabela", "Banco", "Tipo Operação", "Correspondente", "Regras", "Status"];
    const dados = tabelasComissao.map(t => {
      const bancoNome = t.banco_nome || bancos.find(b => b.id === t.banco_id)?.nome || "-";
      return [
        t.nome || t.nome_tabela || "-",
        bancoNome,
        t.tipo_operacao || "-",
        t.correspondente || "Geral",
        t.comissoes?.length || 0,
        t.status || "Ativo"
      ];
    });
    
    return { titulo: "Tabelas de Comissão - Detalhado", colunas, dados };
  };

  const relatorioRegrasComissao = () => {
    const todasRegras = [];
    tabelasComissao.forEach(t => {
      const bancoNome = t.banco_nome || bancos.find(b => b.id === t.banco_id)?.nome || "-";
      (t.comissoes || []).forEach(c => {
        todasRegras.push({
          tabela: t.nome || t.nome_tabela,
          banco: bancoNome,
          tipo: t.tipo_operacao,
          produto: c.produto,
          prazo: c.prazo,
          taxa: c.taxa,
          empresa: c.empresa,
          corretor: c.corretor,
          vigencia: c.dataInicioVigencia
        });
      });
    });
    
    const colunas = ["Produto", "Banco", "Tipo", "Prazo", "Taxa %", "Emp %", "Corr %", "Vigência"];
    const dados = todasRegras.slice(0, 200).map(r => [
      r.produto || "-",
      r.banco || "-",
      r.tipo || "-",
      r.prazo || "-",
      r.taxa ? `${r.taxa}%` : "-",
      r.empresa ? `${r.empresa}%` : "-",
      r.corretor ? `${r.corretor}%` : "-",
      r.vigencia || "-"
    ]);
    
    return { titulo: "Regras de Comissão (Top 200)", colunas, dados };
  };

  // ===== RELATÓRIOS DE CONTRATOS =====
  
  const relatorioContratosPorStatus = () => {
    const agrupado = {};
    contratos.forEach(c => {
      const status = c.status || c.etapa || "Pendente";
      if (!agrupado[status]) agrupado[status] = { qtd: 0, valor: 0 };
      agrupado[status].qtd++;
      agrupado[status].valor += parseFloat(c.valor_liberado || c.valor_solicitado || 0);
    });
    
    const colunas = ["Status", "Quantidade", "Valor Total", "% do Total"];
    const total = contratos.length || 1;
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].qtd - a[1].qtd)
      .map(([status, v]) => [
        status,
        v.qtd,
        `R$ ${v.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        `${((v.qtd / total) * 100).toFixed(1)}%`
      ]);
    
    return { titulo: "Contratos por Status", colunas, dados };
  };

  const relatorioContratosPorBanco = () => {
    const agrupado = {};
    contratos.forEach(c => {
      const banco = c.banco || c.nome_banco || "Não informado";
      if (!agrupado[banco]) agrupado[banco] = { qtd: 0, valor: 0 };
      agrupado[banco].qtd++;
      agrupado[banco].valor += parseFloat(c.valor_liberado || c.valor_solicitado || 0);
    });
    
    const colunas = ["Banco", "Quantidade", "Valor Total", "Ticket Médio"];
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].valor - a[1].valor)
      .map(([banco, v]) => [
        banco,
        v.qtd,
        `R$ ${v.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        `R$ ${(v.valor / v.qtd).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
      ]);
    
    return { titulo: "Contratos por Banco", colunas, dados };
  };

  const relatorioContratosPorTipoOperacao = () => {
    const agrupado = {};
    contratos.forEach(c => {
      const tipo = c.tipo_operacao || "Não informado";
      if (!agrupado[tipo]) agrupado[tipo] = { qtd: 0, valor: 0 };
      agrupado[tipo].qtd++;
      agrupado[tipo].valor += parseFloat(c.valor_liberado || c.valor_solicitado || 0);
    });
    
    const colunas = ["Tipo de Operação", "Quantidade", "Valor Total", "% do Total"];
    const totalValor = Object.values(agrupado).reduce((a, b) => a + b.valor, 0) || 1;
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].valor - a[1].valor)
      .map(([tipo, v]) => [
        tipo,
        v.qtd,
        `R$ ${v.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        `${((v.valor / totalValor) * 100).toFixed(1)}%`
      ]);
    
    return { titulo: "Contratos por Tipo de Operação", colunas, dados };
  };

  const relatorioContratosDetalhado = () => {
    const colunas = ["Nº Contrato", "Cliente", "Banco", "Tipo", "Valor", "Status", "Data"];
    const dados = contratos.slice(0, 100).map(c => [
      c.numero_contrato || "-",
      c.cliente_nome || c.cliente || "-",
      c.banco || c.nome_banco || "-",
      c.tipo_operacao || "-",
      `R$ ${parseFloat(c.valor_liberado || c.valor_solicitado || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
      c.status || c.etapa || "-",
      c.data_contrato ? new Date(c.data_contrato).toLocaleDateString("pt-BR") : "-"
    ]);
    
    return { titulo: "Contratos Detalhado (Últimos 100)", colunas, dados };
  };

  const relatorioComissoesContratos = () => {
    let totalValorContratos = 0;
    let totalComissaoEstimada = 0;
    
    contratos.forEach(c => {
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      const taxaComissao = parseFloat(c.comissao_calculada || c.taxa_comissao || 0) / 100;
      totalValorContratos += valor;
      totalComissaoEstimada += valor * (taxaComissao || 0.03); // 3% default se não informado
    });
    
    const colunas = ["Métrica", "Valor"];
    const dados = [
      ["Total de Contratos", contratos.length],
      ["Valor Total de Contratos", `R$ ${totalValorContratos.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`],
      ["Comissão Estimada Total", `R$ ${totalComissaoEstimada.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`],
      ["Ticket Médio", `R$ ${(totalValorContratos / (contratos.length || 1)).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`],
      ["Comissão Média por Contrato", `R$ ${(totalComissaoEstimada / (contratos.length || 1)).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`]
    ];
    
    return { titulo: "Resumo de Comissões sobre Contratos", colunas, dados };
  };

  // ===== RELATÓRIO: APROVADOS SEM COMISSÃO PAGA =====
  
  const relatorioAprovadosSemComissao = () => {
    // Filtrar contratos que não têm comissão paga/recebida
    // Considera todos os contratos que têm valor mas não têm comissão marcada como paga
    const semComissao = contratos.filter(c => {
      const comissaoPaga = c.comissao_paga || c.comissao_recebida || false;
      const dataComissao = c.data_pagamento_comissao || c.data_comissao_paga || null;
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      
      // Tem valor mas não tem comissão paga
      return valor > 0 && !comissaoPaga && !dataComissao;
    });
    
    const colunas = ["Código", "Cliente", "Banco", "Tipo Operação", "Valor Liberado", "Status", "Data Contrato", "Comissão Estimada"];
    
    const dados = semComissao.map(c => {
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      const taxaComissao = parseFloat(c.comissao_calculada || c.taxa_comissao || 3) / 100; // 3% default
      const comissaoEstimada = valor * taxaComissao;
      
      return [
        c.numero_contrato || c.codigo || c.id?.substring(0, 8) || "-",
        c.cliente_nome || c.cliente || "-",
        c.banco || c.nome_banco || "-",
        c.tipo_operacao || "-",
        `R$ ${valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        c.status || c.etapa || "Pendente",
        c.data_contrato ? new Date(c.data_contrato).toLocaleDateString("pt-BR") : 
          c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : "-",
        `R$ ${comissaoEstimada.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
      ];
    });
    
    // Calcular totais
    const totalValor = semComissao.reduce((acc, c) => acc + parseFloat(c.valor_liberado || c.valor_solicitado || 0), 0);
    const totalComissao = semComissao.reduce((acc, c) => {
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      const taxa = parseFloat(c.comissao_calculada || c.taxa_comissao || 3) / 100;
      return acc + (valor * taxa);
    }, 0);
    
    // Adicionar linha de totais
    if (dados.length > 0) {
      dados.push([
        "TOTAL",
        `${semComissao.length} contratos`,
        "",
        "",
        `R$ ${totalValor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        "",
        "",
        `R$ ${totalComissao.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
      ]);
    }
    
    return { titulo: "Contratos sem Comissão Recebida", colunas, dados };
  };

  const relatorioAprovadosComissaoPendente = () => {
    // Versão resumida por banco - todos os contratos sem comissão paga
    const semComissao = contratos.filter(c => {
      const comissaoPaga = c.comissao_paga || c.comissao_recebida || false;
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      return valor > 0 && !comissaoPaga;
    });
    
    const agrupado = {};
    semComissao.forEach(c => {
      const banco = c.banco || c.nome_banco || "Não informado";
      if (!agrupado[banco]) {
        agrupado[banco] = { qtd: 0, valor: 0, comissao: 0 };
      }
      agrupado[banco].qtd++;
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      agrupado[banco].valor += valor;
      const taxa = parseFloat(c.comissao_calculada || c.taxa_comissao || 3) / 100;
      agrupado[banco].comissao += valor * taxa;
    });
    
    const colunas = ["Banco", "Qtd Contratos", "Valor Total", "Comissão Pendente"];
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1].comissao - a[1].comissao)
      .map(([banco, v]) => [
        banco,
        v.qtd,
        `R$ ${v.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        `R$ ${v.comissao.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
      ]);
    
    // Total geral
    const totalQtd = semComissao.length;
    const totalValor = Object.values(agrupado).reduce((a, b) => a + b.valor, 0);
    const totalComissao = Object.values(agrupado).reduce((a, b) => a + b.comissao, 0);
    
    if (dados.length > 0) {
      dados.push([
        "TOTAL GERAL",
        totalQtd,
        `R$ ${totalValor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        `R$ ${totalComissao.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
      ]);
    }
    
    return { titulo: "Comissões Pendentes por Banco", colunas, dados };
  };

  // ===== RELATÓRIOS DE ÓRGÃOS =====
  
  const relatorioOrgaosPorTipo = () => {
    const agrupado = {};
    orgaos.forEach(o => {
      const tipo = o.tipo || "Outros";
      if (!agrupado[tipo]) agrupado[tipo] = 0;
      agrupado[tipo]++;
    });
    
    const tipoLabels = {
      FEDERAL: "Federal",
      ESTADUAL: "Estadual",
      MUNICIPAL: "Municipal",
      INSS: "INSS",
      MILITAR: "Militar",
      CLT_EMPRESA_PRIVADA: "CLT/Empresa Privada"
    };
    
    const colunas = ["Tipo", "Quantidade", "% do Total"];
    const total = orgaos.length || 1;
    const dados = Object.entries(agrupado)
      .sort((a, b) => b[1] - a[1])
      .map(([tipo, qtd]) => [
        tipoLabels[tipo] || tipo,
        qtd,
        `${((qtd / total) * 100).toFixed(1)}%`
      ]);
    
    return { titulo: "Órgãos por Tipo", colunas, dados };
  };

  const relatorioLotacoesPorOrgao = () => {
    const colunas = ["Órgão", "Tipo", "UF", "Qtd Lotações"];
    const dados = orgaos
      .filter(o => (o.lotacoes?.length || 0) > 0)
      .sort((a, b) => (b.lotacoes?.length || 0) - (a.lotacoes?.length || 0))
      .slice(0, 50)
      .map(o => [
        o.nome,
        o.tipo,
        o.uf,
        o.lotacoes?.length || 0
      ]);
    
    return { titulo: "Lotações por Órgão (Top 50)", colunas, dados };
  };

  const relatorioEspeciesINSS = () => {
    const inssOrgaos = orgaos.filter(o => o.tipo === "INSS");
    const especies = [];
    inssOrgaos.forEach(o => {
      (o.especies || []).forEach(e => {
        especies.push({ ...e, orgao: o.nome });
      });
    });
    
    const colunas = ["Código", "Nome", "Tipo", "Órgão", "Status"];
    const dados = especies.map(e => [
      e.codigo,
      e.nome,
      e.tipo,
      e.orgao,
      e.ativo !== false ? "Ativo" : "Inativo"
    ]);
    
    return { titulo: "Espécies de Benefício (INSS)", colunas, dados };
  };

  // ===== RELATÓRIOS DE DOCUMENTOS =====
  
  const relatorioDocumentosPorCliente = () => {
    const agrupado = {};
    clientes.forEach(c => {
      const docs = c.documentos?.length || 0;
      const faixa = docs === 0 ? "Sem documentos" : docs <= 2 ? "1-2 docs" : docs <= 5 ? "3-5 docs" : "6+ docs";
      if (!agrupado[faixa]) agrupado[faixa] = 0;
      agrupado[faixa]++;
    });
    
    const colunas = ["Faixa de Documentos", "Qtd Clientes", "% do Total"];
    const total = clientes.length || 1;
    const dados = Object.entries(agrupado).map(([faixa, qtd]) => [
      faixa,
      qtd,
      `${((qtd / total) * 100).toFixed(1)}%`
    ]);
    
    return { titulo: "Documentos por Cliente", colunas, dados };
  };

  // Lista de relatórios disponíveis
  const relatoriosDisponiveis = {
    clientes: [
      { id: "clientes_orgao", nome: "Clientes por Órgão/Instituição", icon: Building, fn: relatorioClientesPorOrgao },
      { id: "clientes_uf", nome: "Clientes por Estado (UF)", icon: Users, fn: relatorioClientesPorUF },
      { id: "clientes_beneficio", nome: "Clientes por Tipo de Benefício", icon: FileText, fn: relatorioClientesPorBeneficio },
      { id: "clientes_status", nome: "Clientes por Status", icon: BarChart3, fn: relatorioClientesPorStatus }
    ],
    comissoes: [
      { id: "aprovados_sem_comissao", nome: "⚠️ Contratos sem Comissão Recebida", icon: DollarSign, fn: relatorioAprovadosSemComissao },
      { id: "comissoes_pendentes_banco", nome: "💰 Comissões Pendentes por Banco", icon: Landmark, fn: relatorioAprovadosComissaoPendente },
      { id: "comissoes_banco", nome: "Comissões por Banco", icon: Landmark, fn: relatorioComissoesPorBanco },
      { id: "comissoes_tipo", nome: "Comissões por Tipo de Operação", icon: ClipboardList, fn: relatorioComissoesPorTipoOperacao },
      { id: "comissoes_corresp", nome: "Comissões por Correspondente", icon: Building, fn: relatorioComissoesPorCorrespondente },
      { id: "tabelas_detalhado", nome: "Tabelas de Comissão (Detalhado)", icon: FileSpreadsheet, fn: relatorioTabelasComissaoDetalhado },
      { id: "regras_comissao", nome: "Regras de Comissão (Top 200)", icon: Percent, fn: relatorioRegrasComissao },
      { id: "comissoes_contratos", nome: "Resumo Comissões x Contratos", icon: DollarSign, fn: relatorioComissoesContratos }
    ],
    contratos: [
      { id: "contratos_status", nome: "Contratos por Status", icon: BarChart3, fn: relatorioContratosPorStatus },
      { id: "contratos_banco", nome: "Contratos por Banco", icon: Landmark, fn: relatorioContratosPorBanco },
      { id: "contratos_tipo", nome: "Contratos por Tipo de Operação", icon: ClipboardList, fn: relatorioContratosPorTipoOperacao },
      { id: "contratos_detalhado", nome: "Contratos Detalhado (Últimos 100)", icon: FileText, fn: relatorioContratosDetalhado }
    ],
    financeiro: [
      { id: "comissoes", nome: "Comissões por Período", icon: DollarSign, fn: relatorioComissoes },
      { id: "fluxo_caixa", nome: "Fluxo de Caixa", icon: TrendingUp, fn: relatorioFluxoCaixa },
      { id: "despesas", nome: "Despesas por Categoria", icon: PieChart, fn: relatorioDespesas },
      { id: "receitas_despesas", nome: "Receitas x Despesas", icon: BarChart3, fn: relatorioReceitasDespesas }
    ],
    vendas: [
      { id: "vendas_periodo", nome: "Vendas por Período", icon: Calendar, fn: relatorioVendasPeriodo },
      { id: "vendas_vendedor", nome: "Vendas por Vendedor", icon: Users, fn: relatorioVendasVendedor },
      { id: "propostas_status", nome: "Propostas por Status (Pipeline)", icon: BarChart3, fn: relatorioPropostasStatus },
      { id: "taxa_conversao", nome: "Taxa de Conversão", icon: TrendingUp, fn: relatorioTaxaConversao }
    ],
    orgaos: [
      { id: "orgaos_tipo", nome: "Órgãos por Tipo", icon: Building, fn: relatorioOrgaosPorTipo },
      { id: "lotacoes_orgao", nome: "Lotações por Órgão", icon: FileText, fn: relatorioLotacoesPorOrgao },
      { id: "especies_inss", nome: "Espécies de Benefício (INSS)", icon: FileSpreadsheet, fn: relatorioEspeciesINSS }
    ]
  };

  const handleExportar = (relatorio, formato) => {
    const { titulo, colunas, dados } = relatorio.fn();
    if (formato === "pdf") {
      exportarPDF(titulo, colunas, dados, dados[0]?.length > 5 ? "landscape" : "portrait");
    } else if (formato === "excel") {
      exportarExcel(titulo, colunas, dados);
    } else {
      visualizarRelatorio(titulo, colunas, dados);
    }
  };

  // Paleta de cores por categoria (tab) para o RelatorioCard
  const categoriaPalette = {
    comissoes:   { ring: "ring-violet-200",   bg: "bg-violet-50",   text: "text-violet-700",   accent: "#8b5cf6", soft: "from-violet-50 to-violet-100" },
    contratos:   { ring: "ring-blue-200",     bg: "bg-blue-50",     text: "text-blue-700",     accent: "#3b82f6", soft: "from-blue-50 to-blue-100" },
    clientes:    { ring: "ring-emerald-200",  bg: "bg-emerald-50",  text: "text-emerald-700",  accent: "#10b981", soft: "from-emerald-50 to-emerald-100" },
    financeiro:  { ring: "ring-amber-200",    bg: "bg-amber-50",    text: "text-amber-700",    accent: "#f59e0b", soft: "from-amber-50 to-amber-100" },
    vendas:      { ring: "ring-rose-200",     bg: "bg-rose-50",     text: "text-rose-700",     accent: "#f43f5e", soft: "from-rose-50 to-rose-100" },
    orgaos:      { ring: "ring-cyan-200",     bg: "bg-cyan-50",     text: "text-cyan-700",     accent: "#06b6d4", soft: "from-cyan-50 to-cyan-100" }
  };

  const RelatorioCard = ({ relatorio, categoria = "comissoes" }) => {
    const Icon = relatorio.icon;
    const p = categoriaPalette[categoria] || categoriaPalette.comissoes;
    return (
      <Card className="group relative overflow-hidden border border-slate-200 hover:border-slate-300 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200" data-testid={`relatorio-card-${relatorio.id}`}>
        {/* Faixa lateral colorida */}
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: p.accent }}></div>
        <CardContent className="p-4 pl-5">
          <div className="flex items-start gap-3 mb-3">
            <div className={`p-2.5 rounded-xl ${p.bg} ring-4 ring-transparent group-hover:${p.ring} transition-all`}>
              <Icon className={`h-5 w-5 ${p.text}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm text-slate-800 leading-tight">{relatorio.nome}</h4>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mt-0.5">{categoria}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <Button 
              size="sm" 
              className="gap-1 h-8 text-xs font-semibold text-white shadow-sm hover:shadow-md hover:opacity-90 transition-all w-full"
              style={{ background: p.accent }}
              onClick={() => handleExportar(relatorio, "visualizar")}
              data-testid={`btn-ver-${relatorio.id}`}
            >
              <Eye className="h-3.5 w-3.5" />
              Ver
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-1 h-8 text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 w-full"
              onClick={() => handleExportar(relatorio, "pdf")}
              data-testid={`btn-pdf-${relatorio.id}`}
            >
              <File className="h-3.5 w-3.5" />
              PDF
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-1 h-8 text-xs font-semibold text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 w-full"
              onClick={() => handleExportar(relatorio, "excel")}
              data-testid={`btn-excel-${relatorio.id}`}
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Excel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Relatórios</h1>
          <p className="text-gray-500">Gere relatórios em PDF ou Excel</p>
        </div>
        <Button variant="outline" onClick={carregarDados} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Atualizar Dados
        </Button>
      </div>

      {/* Filtros de Periodo - barrinha compacta */}
      <Card className="border-slate-200" data-testid="filtros-periodo-compact">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Periodo:</span>
            <Input type="date" value={filtros.dataInicio} onChange={e => setFiltros({...filtros, dataInicio: e.target.value})} className="h-8 text-xs w-auto" data-testid="filtro-data-inicio" />
            <span className="text-slate-400 text-xs">ate</span>
            <Input type="date" value={filtros.dataFim} onChange={e => setFiltros({...filtros, dataFim: e.target.value})} className="h-8 text-xs w-auto" data-testid="filtro-data-fim" />
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
              const now = new Date();
              setFiltros({...filtros, dataInicio: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0], dataFim: now.toISOString().split("T")[0]});
            }} data-testid="btn-mes-atual">Mes Atual</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
              const now = new Date();
              setFiltros({...filtros, dataInicio: new Date(now.getFullYear(), 0, 1).toISOString().split("T")[0], dataFim: now.toISOString().split("T")[0]});
            }} data-testid="btn-ano-atual">Ano Atual</Button>
          </div>
        </CardContent>
      </Card>

      {/* Analise Visual - Grafico Dinamico por Dia/Mes/Ano */}
      <Card className="overflow-hidden border-0 shadow-xl" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #312e81 100%)" }} data-testid="card-analise-visual">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2 text-cyan-300 text-xs uppercase tracking-widest font-semibold mb-1">
                <BarChart3 className="h-4 w-4" />
                Analise Visual
              </div>
              <h3 className="text-white text-2xl font-bold leading-tight">Evolucao de Contratos &amp; Comissoes</h3>
              <p className="text-slate-300 text-xs mt-1">Periodo: {new Date(filtros.dataInicio).toLocaleDateString("pt-BR")} - {new Date(filtros.dataFim).toLocaleDateString("pt-BR")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Toggle de Granularidade */}
              <div className="flex bg-slate-800/70 rounded-lg p-1 border border-slate-700" data-testid="chart-granularity">
                {[{v:"dia",l:"Dia"},{v:"mes",l:"Mes"},{v:"ano",l:"Ano"}].map(g => (
                  <button key={g.v} onClick={()=>setChartGranularity(g.v)} data-testid={`btn-gran-${g.v}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${chartGranularity===g.v ? "bg-cyan-500 text-slate-900 shadow-md" : "text-slate-300 hover:bg-slate-700"}`}>
                    {g.l}
                  </button>
                ))}
              </div>
              {/* Toggle de Metrica */}
              <div className="flex bg-slate-800/70 rounded-lg p-1 border border-slate-700" data-testid="chart-metric">
                {[{v:"contratos",l:"Contratos"},{v:"valor",l:"Valor R$"},{v:"comissao",l:"Comissao R$"}].map(m => (
                  <button key={m.v} onClick={()=>setChartMetric(m.v)} data-testid={`btn-metric-${m.v}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${chartMetric===m.v ? "bg-violet-500 text-white shadow-md" : "text-slate-300 hover:bg-slate-700"}`}>
                    {m.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {(() => {
            // Helper: extrai a melhor data do contrato (data_referencia eh a mais comum)
            const getDataContrato = (c) => c.data_referencia || c.data_inicio || c.data_contrato || c.created_at;
            // Helper: extrai o valor financiado real do contrato com varios fallbacks
            const getValorContrato = (c) => {
              const v = parseFloat(c.valor_liberado || c.valor_solicitado || c.saldo_devedor || 0);
              if (v > 0) return v;
              // Ultima tentativa: parcela * quantidade
              const vp = parseFloat(c.valor_parcela || 0);
              const qp = parseInt(c.quantidade_parcelas || 0);
              return vp > 0 && qp > 0 ? vp * qp : 0;
            };
            // Helper: estima comissao usando tabelasComissao (taxa empresa) + valor do contrato
            const taxaEmpresaCache = {};
            const getTaxaEmpresa = (banco, tipoOp) => {
              const k = `${banco}|${tipoOp}`;
              if (taxaEmpresaCache[k] !== undefined) return taxaEmpresaCache[k];
              let melhor = 0;
              (tabelasComissao || []).forEach(t => {
                const matchBanco = (t.banco_nome === banco) || (bancos.find(b => b.id === t.banco_id)?.nome === banco);
                const matchTipo = (t.tipo_operacao === tipoOp);
                if (matchBanco && matchTipo) {
                  (t.comissoes || []).forEach(co => {
                    const e = parseFloat(co.empresa || 0);
                    if (e > melhor) melhor = e;
                  });
                }
              });
              taxaEmpresaCache[k] = melhor;
              return melhor;
            };
            const getComissaoEstimada = (c) => {
              // Se ja existir valor de comissao registrado, usar
              if (c.valor_comissao) return parseFloat(c.valor_comissao);
              const valor = getValorContrato(c);
              if (valor <= 0) return 0;
              const banco = c.banco_nome || c.banco || c.nome_banco || "";
              const tipoOp = c.tipo_operacao || "";
              const taxa = getTaxaEmpresa(banco, tipoOp);
              return valor * (taxa / 100);
            };

            // Agregacao dos dados
            const contratosFiltrados = (contratos || []).filter(c => {
              const d = getDataContrato(c);
              if (!d) return false;
              const dataC = new Date(d).toISOString().split("T")[0];
              return dataC >= filtros.dataInicio && dataC <= filtros.dataFim;
            });
            const buckets = {};
            contratosFiltrados.forEach(c => {
              const raw = getDataContrato(c);
              const dt = new Date(raw);
              if (isNaN(dt.getTime())) return;
              let key;
              if (chartGranularity === "dia") {
                key = dt.toISOString().slice(0,10);
              } else if (chartGranularity === "mes") {
                key = dt.toISOString().slice(0,7);
              } else {
                key = String(dt.getFullYear());
              }
              if (!buckets[key]) buckets[key] = { contratos: 0, valor: 0, comissao: 0 };
              buckets[key].contratos += 1;
              buckets[key].valor += getValorContrato(c);
              buckets[key].comissao += getComissaoEstimada(c);
            });
            const sortedKeys = Object.keys(buckets).sort();
            const data = sortedKeys.map(k => ({ key: k, ...buckets[k] }));
            const maxVal = Math.max(1, ...data.map(d => d[chartMetric] || 0));

            // Totalizadores
            const totalContratos = data.reduce((a, d) => a + d.contratos, 0);
            const totalValor = data.reduce((a, d) => a + d.valor, 0);
            const totalComissao = data.reduce((a, d) => a + d.comissao, 0);
            const ticketMedio = totalContratos > 0 ? totalValor / totalContratos : 0;

            // Formatadores
            const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
            const fmtBucketLabel = (k) => {
              if (chartGranularity === "dia") {
                const [y,m,d] = k.split("-");
                return `${d}/${m}`;
              }
              if (chartGranularity === "mes") {
                const [y,m] = k.split("-");
                const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
                return `${meses[parseInt(m,10)-1]}/${y.slice(2)}`;
              }
              return k;
            };
            const fmtBucketTooltip = (k) => {
              if (chartGranularity === "dia") {
                const [y,m,d] = k.split("-");
                return `${d}/${m}/${y}`;
              }
              if (chartGranularity === "mes") {
                const [y,m] = k.split("-");
                const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
                return `${meses[parseInt(m,10)-1]} de ${y}`;
              }
              return `Ano ${k}`;
            };
            const fmtMetricVal = (v) => {
              if (chartMetric === "contratos") return v.toLocaleString("pt-BR");
              return fmtBRL(v);
            };

            // Dimensoes do grafico
            const chartW = 1100;
            const chartH = 280;
            const padL = 60, padR = 20, padT = 20, padB = 40;
            const innerW = chartW - padL - padR;
            const innerH = chartH - padT - padB;
            const barCount = data.length || 1;
            const slotW = innerW / barCount;
            const barW = Math.max(4, Math.min(56, slotW * 0.72));

            return (
              <>
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <div className="bg-slate-800/50 backdrop-blur border border-slate-700/60 rounded-lg p-3" data-testid="kpi-contratos">
                    <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">Contratos</div>
                    <div className="text-2xl font-bold text-white mt-0.5">{totalContratos.toLocaleString("pt-BR")}</div>
                  </div>
                  <div className="bg-slate-800/50 backdrop-blur border border-slate-700/60 rounded-lg p-3" data-testid="kpi-valor">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">Valor Liberado</div>
                    <div className="text-xl font-bold text-white mt-0.5">{fmtBRL(totalValor)}</div>
                  </div>
                  <div className="bg-slate-800/50 backdrop-blur border border-slate-700/60 rounded-lg p-3" data-testid="kpi-comissao">
                    <div className="text-[10px] uppercase tracking-wider text-violet-400 font-bold">Comissoes</div>
                    <div className="text-xl font-bold text-white mt-0.5">{fmtBRL(totalComissao)}</div>
                  </div>
                  <div className="bg-slate-800/50 backdrop-blur border border-slate-700/60 rounded-lg p-3" data-testid="kpi-ticket">
                    <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Ticket Medio</div>
                    <div className="text-xl font-bold text-white mt-0.5">{fmtBRL(ticketMedio)}</div>
                  </div>
                </div>

                {/* Grafico SVG responsivo */}
                <div className="bg-slate-900/40 rounded-xl border border-slate-700/50 p-3 relative" data-testid="chart-svg-wrapper">
                  {data.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-slate-400 py-16">
                      <BarChart3 className="h-12 w-12 opacity-30 mb-2" />
                      <p className="text-sm">Nenhum contrato no periodo selecionado</p>
                    </div>
                  ) : (
                    <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-[300px]" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chartMetric==="contratos"?"#22d3ee":chartMetric==="valor"?"#34d399":"#a78bfa"} stopOpacity="1"/>
                          <stop offset="100%" stopColor={chartMetric==="contratos"?"#0e7490":chartMetric==="valor"?"#047857":"#5b21b6"} stopOpacity="0.85"/>
                        </linearGradient>
                        <linearGradient id="barHover" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fde047" stopOpacity="1"/>
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.95"/>
                        </linearGradient>
                      </defs>
                      {/* Linhas horizontais (gridlines) */}
                      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
                        const y = padT + innerH * (1 - p);
                        const v = maxVal * p;
                        return (
                          <g key={i}>
                            <line x1={padL} y1={y} x2={chartW - padR} y2={y} stroke="#334155" strokeDasharray="3,4" strokeWidth="1" opacity="0.5"/>
                            <text x={padL - 8} y={y + 3} fontSize="10" fill="#94a3b8" textAnchor="end">
                              {chartMetric==="contratos" ? Math.round(v) : (v >= 1000 ? `R$ ${(v/1000).toFixed(0)}k` : `R$ ${Math.round(v)}`)}
                            </text>
                          </g>
                        );
                      })}
                      {/* Barras */}
                      {data.map((d, i) => {
                        const v = d[chartMetric] || 0;
                        const h = (v / maxVal) * innerH;
                        const x = padL + slotW * i + (slotW - barW) / 2;
                        const y = padT + innerH - h;
                        const isHover = chartHover === i;
                        // Label do valor em cima da barra: formato compacto
                        const valLabel = chartMetric === "contratos"
                          ? v.toLocaleString("pt-BR")
                          : (v >= 1000000 ? `R$ ${(v/1000000).toFixed(1)}M` : v >= 1000 ? `R$ ${(v/1000).toFixed(0)}k` : `R$ ${Math.round(v)}`);
                        // Mostrar label sempre que houver valor (visivel mesmo em barras curtas)
                        const showValLabel = v > 0;
                        return (
                          <g key={d.key}>
                            <rect
                              x={x}
                              y={y}
                              width={barW}
                              height={Math.max(2, h)}
                              fill={isHover ? "url(#barHover)" : "url(#barGrad)"}
                              rx="3"
                              style={{ cursor: "pointer", transition: "fill 0.2s" }}
                              onMouseEnter={() => setChartHover(i)}
                              onMouseLeave={() => setChartHover(null)}
                              data-testid={`bar-${d.key}`}
                            />
                            {/* Valor em cima da barra */}
                            {showValLabel && (
                              <text x={x + barW/2} y={y - 4} fontSize="9" fontWeight="700" fill="#f0f9ff" textAnchor="middle" style={{ paintOrder: "stroke", stroke: "#0f172a", strokeWidth: 3, strokeLinejoin: "round" }}>
                                {valLabel}
                              </text>
                            )}
                            {/* Label X (apenas em alguns para nao sobrecarregar) */}
                            {(barCount <= 14 || i % Math.ceil(barCount/14) === 0) && (
                              <text x={x + barW/2} y={chartH - padB + 14} fontSize="10" fill="#cbd5e1" textAnchor="middle">
                                {fmtBucketLabel(d.key)}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  )}
                  {/* Tooltip */}
                  {chartHover !== null && data[chartHover] && (
                    <div className="absolute top-3 right-3 bg-slate-950/95 border border-slate-600 rounded-lg px-3 py-2 shadow-2xl pointer-events-none" data-testid="chart-tooltip">
                      <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-0.5">{fmtBucketTooltip(data[chartHover].key)}</div>
                      <div className="text-white text-sm font-bold">{fmtMetricVal(data[chartHover][chartMetric])}</div>
                      <div className="text-[10px] text-slate-400 mt-1 space-y-0.5">
                        <div>Contratos: <span className="text-cyan-300">{data[chartHover].contratos}</span></div>
                        <div>Valor: <span className="text-emerald-300">{fmtBRL(data[chartHover].valor)}</span></div>
                        <div>Comissao: <span className="text-violet-300">{fmtBRL(data[chartHover].comissao)}</span></div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Vendas & Indicadores - Relatorio Avancado */}
      <Card className="overflow-hidden border-0 shadow-xl" style={{ background: "linear-gradient(135deg, #0a0e1a 0%, #1a1033 50%, #2d1b4e 100%)" }} data-testid="card-vendas-indicadores">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2 text-fuchsia-300 text-xs uppercase tracking-widest font-semibold mb-1">
                <TrendingUp className="h-4 w-4" />
                Vendas &amp; Indicadores
              </div>
              <h3 className="text-white text-2xl font-bold leading-tight">Performance de Vendas Internas</h3>
              <p className="text-fuchsia-200/70 text-xs mt-1">Contratos do tipo INTERNO no periodo selecionado</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="flex bg-slate-900/70 rounded-lg p-1 border border-fuchsia-900/40" data-testid="vendas-granularity">
                {[{v:"dia",l:"Dia"},{v:"semana",l:"Sem"},{v:"mes",l:"Mes"},{v:"ano",l:"Ano"}].map(g => (
                  <button key={g.v} onClick={()=>setVendasGran(g.v)} data-testid={`btn-vgran-${g.v}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${vendasGran===g.v ? "bg-fuchsia-500 text-white shadow-md" : "text-slate-300 hover:bg-slate-800"}`}>
                    {g.l}
                  </button>
                ))}
              </div>
              <div className="flex bg-slate-900/70 rounded-lg p-1 border border-fuchsia-900/40" data-testid="vendas-metric">
                {[{v:"propostas",l:"Propostas"},{v:"solicitado",l:"Solicitado"},{v:"liberado",l:"Liberado"},{v:"comissao",l:"Comissao"}].map(m => (
                  <button key={m.v} onClick={()=>setVendasMetric(m.v)} data-testid={`btn-vmet-${m.v}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${vendasMetric===m.v ? "bg-cyan-400 text-slate-900 shadow-md" : "text-slate-300 hover:bg-slate-800"}`}>
                    {m.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {(() => {
            // Helpers reutilizados
            const getDataContrato = (c) => c.data_referencia || c.data_inicio || c.data_contrato || c.created_at;
            const getValorContrato = (c) => {
              const v = parseFloat(c.valor_liberado || c.valor_solicitado || c.saldo_devedor || 0);
              if (v > 0) return v;
              const vp = parseFloat(c.valor_parcela || 0);
              const qp = parseInt(c.quantidade_parcelas || 0);
              return vp > 0 && qp > 0 ? vp * qp : 0;
            };
            const taxaCacheV = {};
            const getTaxaEmpresa = (banco, tipoOp) => {
              const k = `${banco}|${tipoOp}`;
              if (taxaCacheV[k] !== undefined) return taxaCacheV[k];
              let melhor = 0;
              (tabelasComissao || []).forEach(t => {
                const matchBanco = (t.banco_nome === banco) || (bancos.find(b => b.id === t.banco_id)?.nome === banco);
                const matchTipo = (t.tipo_operacao === tipoOp);
                if (matchBanco && matchTipo) {
                  (t.comissoes || []).forEach(co => {
                    const e = parseFloat(co.empresa || 0);
                    if (e > melhor) melhor = e;
                  });
                }
              });
              taxaCacheV[k] = melhor;
              return melhor;
            };
            const getComissao = (c) => {
              if (c.valor_comissao) return parseFloat(c.valor_comissao);
              const v = getValorContrato(c);
              if (v <= 0) return 0;
              const banco = c.banco_nome || c.banco || c.nome_banco || "";
              const tipoOp = c.tipo_operacao || "";
              return v * (getTaxaEmpresa(banco, tipoOp) / 100);
            };
            const getValorSolicitado = (c) => parseFloat(c.valor_solicitado || c.valor_liberado || c.saldo_devedor || 0);

            // ISO week helper
            const getISOWeek = (d) => {
              const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
              const dayNum = date.getUTCDay() || 7;
              date.setUTCDate(date.getUTCDate() + 4 - dayNum);
              const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
              const wn = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
              return `${date.getUTCFullYear()}-W${String(wn).padStart(2,"0")}`;
            };

            // Filtra contratos INTERNO no periodo
            const vendasArr = (contratos || []).filter(c => (c.tipo === "INTERNO") && (() => {
              const d = getDataContrato(c);
              if (!d) return false;
              const ds = new Date(d).toISOString().split("T")[0];
              return ds >= filtros.dataInicio && ds <= filtros.dataFim;
            })());

            // Agregacao temporal
            const buckets = {};
            vendasArr.forEach(c => {
              const dt = new Date(getDataContrato(c));
              if (isNaN(dt.getTime())) return;
              let key;
              if (vendasGran === "dia") key = dt.toISOString().slice(0,10);
              else if (vendasGran === "semana") key = getISOWeek(dt);
              else if (vendasGran === "mes") key = dt.toISOString().slice(0,7);
              else key = String(dt.getFullYear());
              if (!buckets[key]) buckets[key] = { propostas: 0, solicitado: 0, liberado: 0, comissao: 0 };
              buckets[key].propostas += 1;
              buckets[key].solicitado += getValorSolicitado(c);
              buckets[key].liberado += getValorContrato(c);
              buckets[key].comissao += getComissao(c);
            });
            const sortedK = Object.keys(buckets).sort();
            const dataTime = sortedK.map(k => ({ key: k, ...buckets[k] }));
            const maxV = Math.max(1, ...dataTime.map(d => d[vendasMetric] || 0));

            // KPIs gerais
            const totalProp = vendasArr.length;
            const totalSol = vendasArr.reduce((a,c) => a + getValorSolicitado(c), 0);
            const totalLib = vendasArr.reduce((a,c) => a + getValorContrato(c), 0);
            const totalCom = vendasArr.reduce((a,c) => a + getComissao(c), 0);
            const aprovados = vendasArr.filter(c => c.situacao === "ATIVO").length;
            const reprovados = vendasArr.filter(c => c.situacao === "REPROVADO").length;
            const taxaAprov = totalProp > 0 ? (aprovados / totalProp) * 100 : 0;

            // Top Bancos por valor liberado
            const bMap = {};
            vendasArr.forEach(c => {
              const b = c.banco_nome || c.banco || "Nao informado";
              if (!bMap[b]) bMap[b] = { propostas: 0, valor: 0, comissao: 0 };
              bMap[b].propostas += 1;
              bMap[b].valor += getValorContrato(c);
              bMap[b].comissao += getComissao(c);
            });
            const topBancos = Object.entries(bMap).map(([b, v]) => ({ nome: b, ...v }))
              .sort((a,b) => b.valor - a.valor).slice(0, 8);
            const maxBanco = Math.max(1, ...topBancos.map(b => b.valor));

            // Top Tipos Operacao
            const tMap = {};
            vendasArr.forEach(c => {
              const t = c.tipo_operacao || "Nao informado";
              const tNorm = t.replace(/_/g, " ").toUpperCase();
              if (!tMap[tNorm]) tMap[tNorm] = { propostas: 0, valor: 0 };
              tMap[tNorm].propostas += 1;
              tMap[tNorm].valor += getValorContrato(c);
            });
            const topTipos = Object.entries(tMap).map(([t, v]) => ({ nome: t, ...v }))
              .sort((a,b) => b.valor - a.valor).slice(0, 8);
            const maxTipo = Math.max(1, ...topTipos.map(t => t.valor));

            // Distribuicao por Situacao
            const sitMap = {};
            vendasArr.forEach(c => {
              const s = c.situacao || "SEM_SITUACAO";
              sitMap[s] = (sitMap[s] || 0) + 1;
            });
            const sitColors = {
              ATIVO: "#22c55e",
              EM_PRODUCAO: "#06b6d4",
              EM_ANALISE: "#f59e0b",
              REPROVADO: "#ef4444",
              ESTORNADO: "#a855f7",
              SEM_SITUACAO: "#64748b"
            };
            const sitArr = Object.entries(sitMap).map(([k,v]) => ({ k, v, c: sitColors[k] || "#94a3b8" }))
              .sort((a,b) => b.v - a.v);
            const sitTotal = sitArr.reduce((a,s)=>a+s.v, 0) || 1;

            // Formatadores
            const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
            const fmtBucketLabel = (k) => {
              if (vendasGran === "dia") {
                const [y,m,d] = k.split("-");
                return `${d}/${m}`;
              }
              if (vendasGran === "semana") {
                const [y,w] = k.split("-W");
                return `S${w}/${y.slice(2)}`;
              }
              if (vendasGran === "mes") {
                const [y,m] = k.split("-");
                const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
                return `${meses[parseInt(m,10)-1]}/${y.slice(2)}`;
              }
              return k;
            };
            const fmtMetric = (v) => vendasMetric === "propostas" ? v.toLocaleString("pt-BR") : fmtBRL(v);

            // Dimensoes do grafico
            const chartW = 1100, chartH = 240, padL = 60, padR = 20, padT = 20, padB = 35;
            const innerW = chartW - padL - padR;
            const innerH = chartH - padT - padB;
            const barCount = dataTime.length || 1;
            const slotW = innerW / barCount;
            const barW = Math.max(4, Math.min(48, slotW * 0.7));

            return (
              <>
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                  <div className="bg-slate-900/50 backdrop-blur border border-fuchsia-900/30 rounded-lg p-3" data-testid="vkpi-prop">
                    <div className="text-[10px] uppercase tracking-wider text-fuchsia-300 font-bold">Propostas</div>
                    <div className="text-2xl font-bold text-white mt-0.5">{totalProp.toLocaleString("pt-BR")}</div>
                  </div>
                  <div className="bg-slate-900/50 backdrop-blur border border-fuchsia-900/30 rounded-lg p-3" data-testid="vkpi-sol">
                    <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">Solicitado</div>
                    <div className="text-lg font-bold text-white mt-0.5">{fmtBRL(totalSol)}</div>
                  </div>
                  <div className="bg-slate-900/50 backdrop-blur border border-fuchsia-900/30 rounded-lg p-3" data-testid="vkpi-lib">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Liberado</div>
                    <div className="text-lg font-bold text-white mt-0.5">{fmtBRL(totalLib)}</div>
                  </div>
                  <div className="bg-slate-900/50 backdrop-blur border border-fuchsia-900/30 rounded-lg p-3" data-testid="vkpi-com">
                    <div className="text-[10px] uppercase tracking-wider text-violet-300 font-bold">Comissao</div>
                    <div className="text-lg font-bold text-white mt-0.5">{fmtBRL(totalCom)}</div>
                  </div>
                  <div className="bg-slate-900/50 backdrop-blur border border-fuchsia-900/30 rounded-lg p-3" data-testid="vkpi-aprov">
                    <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Aprovacao</div>
                    <div className="text-2xl font-bold text-white mt-0.5">{taxaAprov.toFixed(1)}%</div>
                    <div className="text-[9px] text-slate-400">{aprovados}/{totalProp} ativos</div>
                  </div>
                </div>

                {/* Grafico temporal */}
                <div className="bg-slate-950/40 rounded-xl border border-fuchsia-900/20 p-3 mb-4 relative" data-testid="vendas-chart-temporal">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-fuchsia-300 font-bold">Evolucao &mdash; {vendasMetric} por {vendasGran}</div>
                    <div className="text-[10px] text-slate-400">{dataTime.length} {vendasGran === "semana" ? "semanas" : vendasGran+"s"}</div>
                  </div>
                  {dataTime.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-slate-400 py-12">
                      <BarChart3 className="h-10 w-10 opacity-30 mb-2" />
                      <p className="text-sm">Sem vendas internas no periodo</p>
                    </div>
                  ) : (
                    <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-[260px]" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="vendaBarGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f0abfc" stopOpacity="1"/>
                          <stop offset="100%" stopColor="#a21caf" stopOpacity="0.9"/>
                        </linearGradient>
                        <linearGradient id="vendaBarHover" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#67e8f9" stopOpacity="1"/>
                          <stop offset="100%" stopColor="#0891b2" stopOpacity="0.95"/>
                        </linearGradient>
                      </defs>
                      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
                        const y = padT + innerH * (1 - p);
                        const v = maxV * p;
                        return (
                          <g key={i}>
                            <line x1={padL} y1={y} x2={chartW - padR} y2={y} stroke="#3b0764" strokeDasharray="3,4" strokeWidth="1" opacity="0.5"/>
                            <text x={padL - 8} y={y + 3} fontSize="10" fill="#c4b5fd" textAnchor="end">
                              {vendasMetric==="propostas" ? Math.round(v) : (v >= 1000 ? `R$ ${(v/1000).toFixed(0)}k` : `R$ ${Math.round(v)}`)}
                            </text>
                          </g>
                        );
                      })}
                      {dataTime.map((d, i) => {
                        const v = d[vendasMetric] || 0;
                        const h = (v / maxV) * innerH;
                        const x = padL + slotW * i + (slotW - barW) / 2;
                        const y = padT + innerH - h;
                        const isHov = vendasHover === i;
                        const valLabel = vendasMetric === "propostas"
                          ? v.toLocaleString("pt-BR")
                          : (v >= 1000000 ? `R$ ${(v/1000000).toFixed(1)}M` : v >= 1000 ? `R$ ${(v/1000).toFixed(0)}k` : `R$ ${Math.round(v)}`);
                        const showVL = v > 0;
                        return (
                          <g key={d.key}>
                            <rect x={x} y={y} width={barW} height={Math.max(2, h)}
                              fill={isHov ? "url(#vendaBarHover)" : "url(#vendaBarGrad)"}
                              rx="3"
                              style={{ cursor: "pointer", transition: "fill 0.2s" }}
                              onMouseEnter={() => setVendasHover(i)}
                              onMouseLeave={() => setVendasHover(null)}
                              data-testid={`vbar-${d.key}`}
                            />
                            {showVL && (
                              <text x={x + barW/2} y={y - 4} fontSize="9" fontWeight="700" fill="#fdf4ff" textAnchor="middle" style={{ paintOrder: "stroke", stroke: "#1a0033", strokeWidth: 3, strokeLinejoin: "round" }}>
                                {valLabel}
                              </text>
                            )}
                            {(barCount <= 14 || i % Math.ceil(barCount/14) === 0) && (
                              <text x={x + barW/2} y={chartH - padB + 13} fontSize="10" fill="#e9d5ff" textAnchor="middle">
                                {fmtBucketLabel(d.key)}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  )}
                  {vendasHover !== null && dataTime[vendasHover] && (
                    <div className="absolute top-3 right-3 bg-slate-950/95 border border-fuchsia-700 rounded-lg px-3 py-2 shadow-2xl pointer-events-none" data-testid="vendas-tooltip">
                      <div className="text-[10px] uppercase tracking-wider text-fuchsia-300 font-bold">{fmtBucketLabel(dataTime[vendasHover].key)}</div>
                      <div className="text-white text-sm font-bold">{fmtMetric(dataTime[vendasHover][vendasMetric])}</div>
                      <div className="text-[10px] text-slate-400 mt-1 space-y-0.5">
                        <div>Propostas: <span className="text-fuchsia-300">{dataTime[vendasHover].propostas}</span></div>
                        <div>Liberado: <span className="text-emerald-300">{fmtBRL(dataTime[vendasHover].liberado)}</span></div>
                        <div>Comissao: <span className="text-violet-300">{fmtBRL(dataTime[vendasHover].comissao)}</span></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  {/* Top Bancos */}
                  <div className="bg-slate-950/40 rounded-xl border border-fuchsia-900/20 p-3" data-testid="vendas-top-bancos">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-bold">Top Bancos</div>
                      <div className="text-[10px] text-slate-400">Por valor liberado</div>
                    </div>
                    {topBancos.length === 0 ? (
                      <div className="text-slate-500 text-xs text-center py-6">Sem dados</div>
                    ) : (
                      <div className="space-y-2">
                        {topBancos.map((b, i) => {
                          const pct = (b.valor / maxBanco) * 100;
                          const colors = ["#06b6d4","#0891b2","#0e7490","#155e75","#164e63","#0c4a6e","#075985","#0369a1"];
                          return (
                            <div key={b.nome} className="flex items-center gap-2">
                              <div className="w-32 text-[11px] text-slate-200 truncate" title={b.nome}>{b.nome}</div>
                              <div className="flex-1 bg-slate-900/60 rounded h-6 relative">
                                <div className="h-full rounded flex items-center justify-end pr-2 transition-all duration-500" style={{ width: `${Math.max(2, pct)}%`, background: `linear-gradient(90deg, ${colors[i]}aa, ${colors[i]})`, boxShadow: `0 0 10px ${colors[i]}40` }}>
                                  <span className="text-[10px] font-bold text-white whitespace-nowrap">{fmtBRL(b.valor)}</span>
                                </div>
                              </div>
                              <div className="w-12 text-right text-[10px] text-slate-400">{b.propostas}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Top Tabelas/Tipos */}
                  <div className="bg-slate-950/40 rounded-xl border border-fuchsia-900/20 p-3" data-testid="vendas-top-tipos">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-bold">Top Tabelas (Tipo Operacao)</div>
                      <div className="text-[10px] text-slate-400">Por valor liberado</div>
                    </div>
                    {topTipos.length === 0 ? (
                      <div className="text-slate-500 text-xs text-center py-6">Sem dados</div>
                    ) : (
                      <div className="space-y-2">
                        {topTipos.map((t, i) => {
                          const pct = (t.valor / maxTipo) * 100;
                          const colors = ["#10b981","#059669","#047857","#065f46","#064e3b","#022c22","#14b8a6","#0d9488"];
                          return (
                            <div key={t.nome} className="flex items-center gap-2">
                              <div className="w-32 text-[11px] text-slate-200 truncate" title={t.nome}>{t.nome}</div>
                              <div className="flex-1 bg-slate-900/60 rounded h-6 relative">
                                <div className="h-full rounded flex items-center justify-end pr-2 transition-all duration-500" style={{ width: `${Math.max(2, pct)}%`, background: `linear-gradient(90deg, ${colors[i]}aa, ${colors[i]})`, boxShadow: `0 0 10px ${colors[i]}40` }}>
                                  <span className="text-[10px] font-bold text-white whitespace-nowrap">{fmtBRL(t.valor)}</span>
                                </div>
                              </div>
                              <div className="w-12 text-right text-[10px] text-slate-400">{t.propostas}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Distribuicao por Status */}
                {sitArr.length > 0 && (
                  <div className="bg-slate-950/40 rounded-xl border border-fuchsia-900/20 p-3" data-testid="vendas-distrib-status">
                    <div className="text-[11px] uppercase tracking-wider text-amber-300 font-bold mb-3">Distribuicao por Situacao</div>
                    <div className="flex h-9 rounded-lg overflow-hidden mb-3">
                      {sitArr.map(s => {
                        const pct = (s.v / sitTotal) * 100;
                        return (
                          <div key={s.k} className="flex items-center justify-center text-[10px] font-bold text-white transition-all" style={{ width: `${pct}%`, background: s.c, minWidth: "30px" }}>
                            {pct >= 8 ? `${pct.toFixed(0)}%` : ""}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {sitArr.map(s => (
                        <div key={s.k} className="flex items-center gap-1.5 text-xs">
                          <div className="h-3 w-3 rounded" style={{ background: s.c }}></div>
                          <span className="text-slate-300 font-medium">{s.k.replace(/_/g, " ")}:</span>
                          <span className="text-white font-bold">{s.v}</span>
                          <span className="text-slate-500">({((s.v / sitTotal) * 100).toFixed(1)}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Paineis de Analise por Categoria - Comissoes / Contratos / Clientes / Financeiro / Vendas / Orgaos */}
      {(() => {
        const cellToNumber = (cell) => {
          if (cell === null || cell === undefined) return 0;
          if (typeof cell === "number") return cell;
          const s = String(cell).replace(/[^0-9,.\-]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".");
          const n = parseFloat(s);
          return isNaN(n) ? 0 : n;
        };
        const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

        const cfg = {
          comissoes:  { titulo: "Analise de Comissoes",  subtitulo: "Indicadores de Comissoes em Graficos", icon: DollarSign,    iconColor: "text-amber-300",   bg: "linear-gradient(135deg, #1e1b4b 0%, #1e293b 50%, #0c4a6e 100%)", colors: ["#ef4444","#f59e0b","#06b6d4","#10b981","#a855f7","#3b82f6","#ec4899","#14b8a6"] },
          contratos:  { titulo: "Analise de Contratos",  subtitulo: "Indicadores Visuais de Contratos",     icon: ClipboardList, iconColor: "text-blue-300",    bg: "linear-gradient(135deg, #0c1e3a 0%, #1e293b 50%, #1e3a8a 100%)", colors: ["#3b82f6","#06b6d4","#8b5cf6","#10b981","#f59e0b","#ef4444"] },
          clientes:   { titulo: "Analise de Clientes",   subtitulo: "Indicadores Visuais de Clientes",      icon: Users,         iconColor: "text-emerald-300", bg: "linear-gradient(135deg, #022c22 0%, #1e293b 50%, #064e3b 100%)", colors: ["#10b981","#14b8a6","#06b6d4","#84cc16"] },
          financeiro: { titulo: "Analise Financeira",    subtitulo: "Indicadores Visuais Financeiros",      icon: TrendingUp,    iconColor: "text-amber-300",   bg: "linear-gradient(135deg, #451a03 0%, #1e293b 50%, #78350f 100%)", colors: ["#f59e0b","#ef4444","#10b981","#06b6d4"] },
          vendas:     { titulo: "Analise de Vendas",     subtitulo: "Indicadores Visuais de Vendas",        icon: TrendingUp,    iconColor: "text-rose-300",    bg: "linear-gradient(135deg, #4c0519 0%, #1e293b 50%, #881337 100%)", colors: ["#f43f5e","#ec4899","#a855f7","#06b6d4"] },
          orgaos:     { titulo: "Analise de Orgaos",     subtitulo: "Indicadores Visuais de Orgaos",        icon: Building,      iconColor: "text-cyan-300",    bg: "linear-gradient(135deg, #0c4a6e 0%, #1e293b 50%, #164e63 100%)", colors: ["#06b6d4","#0891b2","#3b82f6","#8b5cf6"] }
        };
        const ordem = ["comissoes", "contratos", "clientes", "financeiro", "vendas", "orgaos"];

        const renderPainel = (catKey) => {
          const c = cfg[catKey];
          const lista = relatoriosDisponiveis[catKey] || [];
          if (!c || lista.length === 0) return null;
          const Icon = c.icon;
          return (
            <Card key={catKey} className="overflow-hidden border-0 shadow-xl" style={{ background: c.bg }} data-testid={`painel-${catKey}`}>
              <CardContent className="p-6">
                <div className="mb-5">
                  <div className={`flex items-center gap-2 ${c.iconColor} text-xs uppercase tracking-widest font-semibold mb-1`}>
                    <Icon className="h-4 w-4" />
                    {c.titulo}
                  </div>
                  <h3 className="text-white text-2xl font-bold leading-tight">{c.subtitulo}</h3>
                  <p className="text-slate-300/70 text-xs mt-1">Mini-graficos resumidos. Clique em um card para abrir o relatorio completo com tabela.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  {lista.map((rel, idx) => {
                    let dadosBruto = { titulo: rel.nome, colunas: [], dados: [] };
                    try { dadosBruto = rel.fn() || dadosBruto; } catch (e) { dadosBruto = { ...dadosBruto, dados: [] }; }
                    const cols = dadosBruto.colunas || [];
                    const rows = dadosBruto.dados || [];
                    let metricCol = -1;
                    let isCurrency = false;
                    for (let ci = 1; ci < cols.length; ci++) {
                      const valid = rows.filter(r => cellToNumber(r[ci]) > 0).length;
                      if (valid >= Math.max(1, rows.length * 0.5)) {
                        metricCol = ci;
                        const sample = String(rows[0]?.[ci] || "");
                        isCurrency = sample.includes("R$") || sample.includes("$");
                        break;
                      }
                    }
                    const top = metricCol >= 0
                      ? rows.map(r => ({ label: String(r[0] ?? "-"), value: cellToNumber(r[metricCol]) }))
                          .filter(d => d.value > 0)
                          .sort((a,b) => b.value - a.value)
                          .slice(0, 5)
                      : [];
                    const maxV = Math.max(1, ...top.map(d => d.value));
                    const total = rows.reduce((acc, r) => acc + (metricCol >= 0 ? cellToNumber(r[metricCol]) : 0), 0);
                    const color = c.colors[idx % c.colors.length];
                    const colorSoft = color + "30";
                    const tituloLimpo = rel.nome.replace(/^[^A-Za-z0-9]+/u, "").trim();

                    return (
                      <div key={rel.id}
                        className="rounded-xl border border-slate-700/40 p-3 cursor-pointer hover:border-slate-500 hover:-translate-y-0.5 transition-all group"
                        style={{ background: `linear-gradient(135deg, ${colorSoft}, #0f172a 80%)` }}
                        onClick={() => visualizarRelatorio(dadosBruto.titulo || rel.nome, cols, rows)}
                        data-testid={`mini-${catKey}-${rel.id}`}
                      >
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="text-[10px] uppercase tracking-wider font-bold leading-tight" style={{ color }}>{tituloLimpo}</div>
                          <Eye className="h-3 w-3 text-slate-500 group-hover:text-white transition-colors flex-shrink-0" />
                        </div>
                        <div className="flex items-baseline gap-2 mb-2">
                          <div className="text-2xl font-bold text-white">{rows.length}</div>
                          <div className="text-[10px] text-slate-400 uppercase">linhas</div>
                          {metricCol >= 0 && total > 0 && (
                            <div className="ml-auto text-right">
                              <div className="text-[9px] uppercase text-slate-500 truncate max-w-[80px]">{cols[metricCol]}</div>
                              <div className="text-xs font-bold text-white">{isCurrency ? fmtBRL(total) : total.toLocaleString("pt-BR")}</div>
                            </div>
                          )}
                        </div>
                        {top.length > 0 ? (
                          <div className="space-y-1">
                            {top.map((d, i) => {
                              const pct = (d.value / maxV) * 100;
                              return (
                                <div key={i} className="flex items-center gap-1.5">
                                  <div className="w-20 text-[9px] text-slate-300 truncate" title={d.label}>{d.label}</div>
                                  <div className="flex-1 bg-slate-900/70 rounded h-3.5 relative overflow-hidden">
                                    <div className="h-full rounded transition-all duration-700" style={{ width: `${Math.max(3, pct)}%`, background: `linear-gradient(90deg, ${color}aa, ${color})`, boxShadow: `0 0 6px ${color}55` }}></div>
                                  </div>
                                  <div className="w-16 text-right text-[9px] font-bold" style={{ color }}>
                                    {isCurrency ? (d.value >= 1000 ? `R$ ${(d.value/1000).toFixed(0)}k` : `R$ ${Math.round(d.value)}`) : d.value.toLocaleString("pt-BR")}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-500 text-center py-3">Sem dados no periodo</div>
                        )}
                        <div className="flex gap-1 mt-2 pt-2 border-t border-slate-700/40">
                          <button onClick={(e) => { e.stopPropagation(); handleExportar(rel, "pdf"); }} className="flex-1 text-[9px] font-semibold text-red-400 hover:bg-red-500/10 rounded py-1 transition" data-testid={`mini-pdf-${rel.id}`}>PDF</button>
                          <button onClick={(e) => { e.stopPropagation(); handleExportar(rel, "excel"); }} className="flex-1 text-[9px] font-semibold text-emerald-400 hover:bg-emerald-500/10 rounded py-1 transition" data-testid={`mini-excel-${rel.id}`}>Excel</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        };

        return ordem.map(k => renderPainel(k));
      })()}

      {/* Tabs de Relatórios */}

      {/* Dialog de Preview */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewTitle}</DialogTitle>
            <DialogDescription>
              Visualização do relatório - {new Date().toLocaleDateString("pt-BR")}
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (() => {
            // Helper: extrai numero de uma celula (string "R$ 1.234,56" ou "15%" ou plain number)
            const cellToNumber = (cell) => {
              if (cell === null || cell === undefined) return NaN;
              if (typeof cell === "number") return cell;
              const s = String(cell).replace(/[^0-9,.\-]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".");
              const n = parseFloat(s);
              return isNaN(n) ? NaN : n;
            };
            // Identificar coluna numerica (primeira coluna depois da label que tem >=70% valores numericos)
            const colCount = previewData.colunas.length;
            let metricColIdx = -1;
            let metricColIsCurrency = false;
            for (let ci = 1; ci < colCount; ci++) {
              const vals = previewData.dados.map(r => cellToNumber(r[ci]));
              const validCount = vals.filter(v => !isNaN(v)).length;
              if (validCount >= Math.max(1, previewData.dados.length * 0.7)) {
                metricColIdx = ci;
                const sample = String(previewData.dados[0]?.[ci] || "");
                metricColIsCurrency = sample.includes("R$") || sample.includes("$");
                break;
              }
            }
            // Construir dados do grafico (top 12)
            const chartData = metricColIdx >= 0 ? previewData.dados
              .map(r => ({ label: String(r[0] ?? "-"), value: cellToNumber(r[metricColIdx]) || 0 }))
              .filter(d => d.value > 0)
              .sort((a, b) => b.value - a.value)
              .slice(0, 12) : [];
            const maxV = Math.max(1, ...chartData.map(d => d.value));
            const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
            const fmtVal = (v) => metricColIsCurrency ? fmtBRL(v) : v.toLocaleString("pt-BR");
            // Paleta de cores do grafico (rotativa)
            const palette = ["#8b5cf6","#06b6d4","#10b981","#f59e0b","#ef4444","#ec4899","#3b82f6","#14b8a6","#f97316","#a855f7","#22c55e","#0ea5e9"];

            return (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {/* Grafico de barras horizontais (top 12) */}
                {chartData.length > 0 && (
                  <div className="rounded-xl border-0 p-4" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #312e81 100%)" }} data-testid="preview-chart">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-cyan-300">Visualizacao Grafica</div>
                        <div className="text-white text-sm font-semibold">Top {chartData.length} - {previewData.colunas[metricColIdx]}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Total</div>
                        <div className="text-white text-base font-bold">{fmtVal(chartData.reduce((a,d)=>a+d.value,0))}</div>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {chartData.map((d, i) => {
                        const pct = (d.value / maxV) * 100;
                        const color = palette[i % palette.length];
                        return (
                          <div key={i} className="flex items-center gap-2" data-testid={`preview-bar-${i}`}>
                            <div className="w-32 text-[11px] text-slate-200 truncate" title={d.label}>{d.label}</div>
                            <div className="flex-1 bg-slate-800/60 rounded h-6 relative overflow-hidden">
                              <div className="h-full rounded transition-all duration-500 flex items-center justify-end pr-2"
                                style={{ width: `${Math.max(2, pct)}%`, background: `linear-gradient(90deg, ${color}aa, ${color})`, boxShadow: `0 0 12px ${color}55` }}>
                                <span className="text-[10px] font-bold text-white drop-shadow">{fmtVal(d.value)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tabela de dados */}
                <ScrollArea className="h-[260px] border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {previewData.colunas.map((col, i) => (
                          <TableHead key={i}>{col}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.dados.length > 0 ? (
                        previewData.dados.map((row, i) => (
                          <TableRow key={i}>
                            {row.map((cell, j) => (
                              <TableCell key={j}>{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={previewData.colunas.length} className="text-center py-8 text-gray-500">
                            Nenhum dado encontrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            );
          })()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
            <Button 
              variant="outline" 
              className="gap-1 text-red-600"
              onClick={() => {
                const { colunas, dados } = previewData;
                exportarPDF(previewTitle, colunas, dados);
              }}
            >
              <File className="h-4 w-4" />
              Exportar PDF
            </Button>
            <Button 
              className="gap-1 bg-green-600 hover:bg-green-700"
              onClick={() => {
                const { colunas, dados } = previewData;
                exportarExcel(previewTitle, colunas, dados);
              }}
            >
              <FileSpreadsheet className="h-4 w-4" />
              Exportar Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
